import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation } from '../../api/hooks';
import type { CloseDrawerRequest, DrawerCountResponse, MoneyPayload } from '../../api/types';
import { ErrorState } from '../../components/states/StateViews';

/**
 * Counting the drawer at the end of the day (FR-908).
 *
 * The note is only asked for when the count does not match, and then it is required — by the
 * server, not only here. A drawer that is fifty short with an explanation is an ordinary day; one
 * that is fifty short in silence is the first row of a pattern nobody can investigate later.
 */
export function CloseDrawer({
  clinicId,
  branchId,
  day,
  expected,
}: {
  readonly clinicId: string;
  readonly branchId: string;
  readonly day: string;
  readonly expected?: MoneyPayload;
}) {
  const { t } = useTranslation();
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');

  const close = useApiMutation<DrawerCountResponse, CloseDrawerRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/cash-summary/close?branchId=${branchId}&day=${day}`,
      options: { method: 'POST', body },
    }),
    {
      invalidates: [queryKeys.cashSummary(clinicId, branchId, day)],
      onSuccess: () => {
        setCounted('');
        setNote('');
      },
    },
  );

  const differs = counted !== '' && Number(counted).toFixed(2) !== (expected?.amount ?? '0.00');

  return (
    <form
      className="close-drawer"
      onSubmit={(event) => {
        event.preventDefault();
        close.mutate({
          counted: { amount: Number(counted).toFixed(2), currency: 'EGP' },
          note: note || undefined,
        });
      }}
    >
      <label className="field">
        <span>{t('sales.counted')}</span>
        <input
          type="number"
          step="0.01"
          min={0}
          value={counted}
          onChange={(event) => setCounted(event.target.value)}
          required
        />
      </label>

      {differs ? (
        <label className="field">
          <span>{t('sales.varianceNote')}</span>
          <input value={note} onChange={(event) => setNote(event.target.value)} required />
        </label>
      ) : null}

      {close.isError ? <ErrorState error={close.error} /> : null}

      <button type="submit" className="primary" disabled={close.isPending || counted === ''}>
        {t('sales.close')}
      </button>
    </form>
  );
}
